import {
  BlockchainNetwork,
  ConstantsHelper,
  ParsedRateLimiter,
  POSITION_LIMIT,
  SOL,
  U64_MAX,
  WalletType,
} from "@renec-foundation/relend-adapter"
import BigNumber from "bignumber.js"

import { ObligationType } from "@/atoms/obligations"
import { SelectedReserveType } from "@/atoms/shared"
import { NATIVE_MINT } from "@solana/spl-token"
import { RENEC } from "../constants"
import { replaceLocaleStringWithValues } from "../helpers"
import {
  FIAT_DECIMALS,
  RENEC_PADDING_FOR_RENT_AND_FEE,
  SOL_PADDING_FOR_RENT_AND_FEE,
  sufficientNativeTokenForTransaction,
} from "./commons"

const REVND_MIN_BORROW_FEE = 2
const HOST_FEE_OWNER_AND_HOST_UNIT = 2
const HOST_FEE_OWNER_ONLY_UNIT = 2

// When we borrow, prices may fluctuate. This value is used to minus the estimated error value when that happens.
export const BORROW_MAX_LIMIT_ERROR_PERCENTAGE = 0.005 // 0.015%

export const borrowConfigs = {
  verifyAction: (
    network: BlockchainNetwork,
    value: BigNumber,
    obligation: ObligationType | null,
    reserve: SelectedReserveType,
    wallet: WalletType,
    rateLimiter: ParsedRateLimiter | null,
    trans: any,
  ) => {
    if (value.isZero() || !value) {
      return trans.dashboard.modal.errors.please_enter_amount
    }

    if (!obligation) return null

    const isFiatToken = reserve.decimals === FIAT_DECIMALS
    const isRenecChain = ConstantsHelper.isRenecChain(network)
    const isSolanaChain = ConstantsHelper.isSolanaChain(network)

    let borrowableAmountUntil95utilization = new BigNumber(reserve.availableAmount)

    if (isFiatToken && reserve.availableAmount.multipliedBy(reserve.borrowFee).isLessThan(REVND_MIN_BORROW_FEE)) {
      borrowableAmountUntil95utilization = borrowableAmountUntil95utilization.minus(2)
    } else {
      borrowableAmountUntil95utilization = borrowableAmountUntil95utilization.multipliedBy(
        BigNumber(1).minus(reserve.borrowFee),
      )
    }

    const hostFee =
      reserve.hostFee > 0
        ? HOST_FEE_OWNER_AND_HOST_UNIT / 10 ** reserve.decimals
        : HOST_FEE_OWNER_ONLY_UNIT / 10 ** reserve.decimals

    let overBorrowLimit = obligation.minPriceBorrowLimit
      .minus(obligation.maxPriceUserTotalWeightedBorrow)
      .dividedBy(reserve.maxPrice)
      .dividedBy(reserve.borrowWeight)

    // BORROW_MAX_LIMIT_ERROR_PERCENTAGE For price fluctuations and accure interest on-chain
    if (isFiatToken && overBorrowLimit.multipliedBy(reserve.borrowFee).isLessThan(REVND_MIN_BORROW_FEE)) {
      overBorrowLimit = overBorrowLimit
        .minus(2)
        .minus(hostFee)
        .multipliedBy(BigNumber(1).minus(BORROW_MAX_LIMIT_ERROR_PERCENTAGE))
    } else {
      overBorrowLimit = overBorrowLimit
        .multipliedBy(BigNumber(1).minus(reserve.borrowFee))
        .minus(hostFee)
        .multipliedBy(BigNumber(1).minus(BORROW_MAX_LIMIT_ERROR_PERCENTAGE))
    }

    // Allow action despite position limit, provided user already has a position in this asset
    const positionLimitReached =
      obligation.positions > POSITION_LIMIT &&
      !obligation.borrows.map((d) => d.reserveAddress).includes(reserve.address)

    const reserveRateLimit =
      reserve.rateLimiter.remainingOutflow
        ?.dividedBy(new BigNumber(10 ** reserve.decimals))
        .multipliedBy(BigNumber(1).minus(BORROW_MAX_LIMIT_ERROR_PERCENTAGE)) ?? new BigNumber(U64_MAX) // BORROW_MAX_LIMIT_ERROR_PERCENTAGE For price fluctuations and accure interest on-chain

    const poolRateLimit =
      rateLimiter?.remainingOutflow
        ?.dividedBy(reserve.maxPrice)
        .multipliedBy(BigNumber(1).minus(BORROW_MAX_LIMIT_ERROR_PERCENTAGE)) ?? new BigNumber(U64_MAX) // BORROW_MAX_LIMIT_ERROR_PERCENTAGE For price fluctuations and accure interest on-chain

    const valueRounded = value.decimalPlaces(reserve.decimals)

    // for reVND
    const roundingStrategy = isFiatToken ? BigNumber.ROUND_DOWN : undefined

    const amountPadding = isRenecChain ? RENEC_PADDING_FOR_RENT_AND_FEE : SOL_PADDING_FOR_RENT_AND_FEE
    const isSolanaInSufficient = isSolanaChain && !sufficientNativeTokenForTransaction(network, wallet)
    const nativeMintBalance =
      wallet.find((ass) => ass.mintAddress === NATIVE_MINT.toBase58())?.amount ?? new BigNumber(0)
    const minNativeFee = isSolanaChain ? new BigNumber(amountPadding).minus(nativeMintBalance).toString() : null

    if (positionLimitReached) {
      return trans.dashboard.modal.errors.max_positions_reached
    }
    if (valueRounded.isGreaterThan(reserve.availableAmount.decimalPlaces(reserve.decimals, roundingStrategy))) {
      return trans.dashboard.modal.errors.insufficient_liquidity_borrow
    }
    if (
      obligation?.maxPriceUserTotalWeightedBorrow
        .plus(valueRounded)
        .isGreaterThanOrEqualTo(reserve.reserveBorrowCap.decimalPlaces(reserve.decimals, roundingStrategy))
    ) {
      return trans.dashboard.modal.errors.over_reserve_borrow_limit
    }
    if (
      valueRounded.isGreaterThan(borrowableAmountUntil95utilization.decimalPlaces(reserve.decimals, roundingStrategy))
    ) {
      return trans.dashboard.modal.errors.cannot_borrow_utilization
    }
    if (valueRounded.isGreaterThan(overBorrowLimit.decimalPlaces(reserve.decimals, roundingStrategy))) {
      return trans.dashboard.modal.errors.exceeds_borrow_limit
    }
    if (valueRounded.isGreaterThan(poolRateLimit.decimalPlaces(reserve.decimals, roundingStrategy))) {
      return trans.dashboard.modal.errors.pool_outflow_rate_limit
    }
    if (valueRounded.isGreaterThan(reserveRateLimit.decimalPlaces(reserve.decimals, roundingStrategy))) {
      return trans.dashboard.modal.errors.reserve_outflow_rate_limit
    }
    if (isSolanaInSufficient && minNativeFee) {
      return replaceLocaleStringWithValues(
        trans.dashboard.modal.errors.min_native_fee,
        minNativeFee,
        isRenecChain ? RENEC : SOL,
      )
    }
    return null
  },
  getNewCalculations: (obligation: ObligationType | null, reserve: SelectedReserveType, value: string) => {
    if (!obligation) {
      return {
        borrowLimit: null,
        newBorrowLimit: null,
        utilization: null,
        newBorrowUtilization: null,
        calculatedBorrowFee: null,
      }
    }

    const isFiatToken = reserve.decimals === FIAT_DECIMALS

    const valueObj = new BigNumber(value)

    const utilization = !obligation.totalSupplyValue.isZero()
      ? obligation.totalBorrowValue.dividedBy(obligation.totalSupplyValue)
      : null

    const newBorrowBalance = obligation.totalBorrowValue.plus(valueObj.times(reserve.price))

    const newBorrowUtilization =
      obligation.totalSupplyValue && !obligation.totalSupplyValue.isZero()
        ? newBorrowBalance.dividedBy(obligation.totalSupplyValue)
        : null

    let calculatedBorrowFee = valueObj.isNaN() ? null : valueObj.times(new BigNumber(reserve.borrowFee))

    if (isFiatToken && calculatedBorrowFee?.isLessThan(REVND_MIN_BORROW_FEE)) {
      calculatedBorrowFee = BigNumber(REVND_MIN_BORROW_FEE)
    }

    return {
      borrowLimit: obligation.minPriceBorrowLimit,
      newBorrowLimit: null,
      utilization: utilization,
      newBorrowUtilization,
      calculatedBorrowFee,
    }
  },
  calculateMax: (
    reserve: SelectedReserveType,
    _wallet: WalletType,
    obligation: ObligationType | null,
    rateLimiter: ParsedRateLimiter | null,
  ) => {
    if (!obligation) {
      return new BigNumber(0)
    }

    const isFiatToken = reserve.decimals === FIAT_DECIMALS

    const borrowCapRemaining = reserve.reserveBorrowCap.minus(reserve.totalBorrow)

    const hostFee =
      reserve.hostFee > 0
        ? HOST_FEE_OWNER_AND_HOST_UNIT / 10 ** reserve.decimals
        : HOST_FEE_OWNER_ONLY_UNIT / 10 ** reserve.decimals

    let borrowableAmountUntil95utilization = new BigNumber(reserve.availableAmount)

    if (isFiatToken && reserve.availableAmount.multipliedBy(reserve.borrowFee).isLessThan(REVND_MIN_BORROW_FEE)) {
      borrowableAmountUntil95utilization = borrowableAmountUntil95utilization.minus(2)
    } else {
      borrowableAmountUntil95utilization = borrowableAmountUntil95utilization
        .multipliedBy(BigNumber(1).minus(reserve.borrowFee))
        .minus(hostFee)
    }

    const reserveRateLimit =
      reserve.rateLimiter.remainingOutflow?.dividedBy(new BigNumber(10 ** reserve.decimals)) ?? new BigNumber(U64_MAX)

    const poolRateLimit = rateLimiter?.remainingOutflow?.dividedBy(reserve.maxPrice) ?? new BigNumber(U64_MAX)

    let obligationBorrowLimit = obligation.minPriceBorrowLimit
      .minus(obligation.maxPriceUserTotalWeightedBorrow)
      .dividedBy(reserve.maxPrice)
      .dividedBy(reserve.borrowWeight)

    if (isFiatToken && obligationBorrowLimit.multipliedBy(reserve.borrowFee).isLessThan(REVND_MIN_BORROW_FEE)) {
      obligationBorrowLimit = obligationBorrowLimit.minus(2).minus(hostFee)
    } else {
      obligationBorrowLimit = obligationBorrowLimit.multipliedBy(BigNumber(1).minus(reserve.borrowFee)).minus(hostFee)
    }

    // for reVND
    const roundingStrategy = isFiatToken ? BigNumber.ROUND_DOWN : undefined
    const actualMaxBorrowable = BigNumber.max(
      BigNumber.min(
        obligationBorrowLimit,
        borrowableAmountUntil95utilization,
        borrowCapRemaining,
        poolRateLimit,
        reserveRateLimit,
      ),
      BigNumber(0),
    )

    let maxBorrowableWithError = actualMaxBorrowable.multipliedBy(BigNumber(1).minus(BORROW_MAX_LIMIT_ERROR_PERCENTAGE)) // BORROW_MAX_LIMIT_ERROR_PERCENTAGE For price fluctuations and accure interest on-chain
    maxBorrowableWithError = maxBorrowableWithError.decimalPlaces(reserve.decimals, roundingStrategy) // ROUND THE NUMBER DOWN IF IS REVND

    return maxBorrowableWithError
  },
  calculateMax2: (
    reserve: SelectedReserveType,
    _wallet: WalletType,
    obligation: ObligationType | null,
    rateLimiter: ParsedRateLimiter | null,
  ) => {
    if (!obligation) {
      return new BigNumber(0)
    }

    const borrowCapRemaining = reserve.reserveBorrowCap.minus(reserve.totalBorrow)

    const borrowableAmountUntil95utilization = new BigNumber(reserve.availableAmount).minus(
      new BigNumber(reserve.totalSupply).times(new BigNumber(0.05)),
    )

    const reserveRateLimit =
      reserve.rateLimiter.remainingOutflow?.dividedBy(new BigNumber(10 ** reserve.decimals)) ?? new BigNumber(U64_MAX)

    const poolRateLimit = rateLimiter?.remainingOutflow?.dividedBy(reserve.maxPrice) ?? new BigNumber(U64_MAX)

    return BigNumber.max(
      BigNumber.min(
        obligation.minPriceBorrowLimit
          .minus(obligation.maxPriceUserTotalWeightedBorrow)
          .dividedBy(reserve.maxPrice)
          .dividedBy(reserve.borrowWeight),
        borrowableAmountUntil95utilization,
        borrowCapRemaining,
        poolRateLimit,
        reserveRateLimit,
      ),
      BigNumber(0),
    ).decimalPlaces(reserve.decimals)
  },
}
